/**
 * Generative music scheduler. A lookahead clock (sixteenth-note grid, ~0.12 s ahead of the
 * context clock) driven from the engine tick. Tempo, percussion level and melody density follow
 * the run's speed + flow, but only ever change on bar lines, and only by small steps, so the
 * music swells with the player without lurching.
 */
import type { ThemeDef } from '../themes';
import type { AudioState } from '../types';
import { approach, clamp, clamp01 } from '../util';
import type { BarInfo, Composer, MusicCore } from './composer';
import { DunesComposer } from './dunes';
import { SkyComposer } from './sky';

const LOOKAHEAD = 0.12;
/** Steps older than this are skipped rather than played late (frame hitch). */
const LATE = 0.03;
const MAX_BPM_STEP = 4;

export class Music {
  private composer: Composer | null = null;
  private composerReady = false;
  private theme: ThemeDef | null = null;
  private nextT = 0;
  private stepIdx = 0;
  private bpm = 84;
  private intensity = 0;
  private state: AudioState = 'idle';
  private restart = true;
  private readonly info: BarInfo = {
    t: 0,
    bpm: 84,
    barDur: 240 / 84,
    sd: 15 / 84,
    intensity: 0,
    density: 0.15,
    percLevel: 0,
    playing: false,
    restart: true,
  };

  constructor(private readonly core: MusicCore) {}

  setTheme(theme: ThemeDef, now: number, fade: number): void {
    const old = this.composer;
    if (old) {
      const end = old.route.fade(0, now, fade);
      this.core.later.add(end + 0.3, () => old.dispose());
    } else {
      this.bpm = theme.idleBpm;
      this.setTempo(this.bpm);
      this.nextT = now + 0.1;
      this.stepIdx = 0;
    }
    this.theme = theme;
    this.composer = theme.id === 'dunes' ? new DunesComposer(this.core, theme, now) : new SkyComposer(this.core, theme, now);
    this.composer.route.fade(1, now, Math.max(0.3, fade));
    this.composerReady = false; // starts on the next bar line
    this.restart = true;
  }

  update(state: AudioState, speedNorm: number, flow: number, dt: number): void {
    this.state = state;
    if (state === 'paused') return;
    const target = state === 'playing' ? clamp01(0.22 + 0.45 * clamp01(speedNorm) + 0.45 * clamp01(flow)) : 0;
    this.intensity = approach(this.intensity, target, dt, target > this.intensity ? 2.5 : 1.2);
  }

  /** A run starts: restart the phrase on a fresh downbeat just after the start swoosh. */
  start(now: number): void {
    this.nextT = now + 0.32;
    this.stepIdx = 0;
    this.restart = true;
  }

  /** Crash / caught: drop the groove immediately (the bus low-pass handles the rest). */
  crash(): void {
    this.info.percLevel = 0;
    this.info.density = 0.08;
  }

  private setTempo(bpm: number): void {
    this.info.bpm = bpm;
    this.info.barDur = 240 / bpm;
    this.info.sd = 15 / bpm;
  }

  private beginBar(t: number, now: number): void {
    const theme = this.theme;
    const comp = this.composer;
    if (!theme || !comp) return;
    const playing = this.state === 'playing';
    const paused = this.state === 'paused';
    const I = this.intensity;

    if (!paused) {
      const [lo, hi] = theme.bpm;
      const target = playing ? lo + (hi - lo) * I : theme.idleBpm;
      this.bpm += clamp(Math.round(target) - this.bpm, -MAX_BPM_STEP, MAX_BPM_STEP);
      this.setTempo(this.bpm);

      const want = playing ? (I < 0.4 ? 1 : I < 0.6 ? 2 : I < 0.8 ? 3 : 4) : 0;
      const cur = this.info.percLevel;
      this.info.percLevel = want === 0 ? 0 : cur + Math.sign(want - cur);
      this.info.density = playing ? 0.3 + 0.55 * I : this.state === 'crashed' ? 0.08 : 0.16;
    }
    // Never start a bar's long envelopes in the past (after a hitch) — they would jump in mid-ramp.
    this.info.t = Math.max(t, now + 0.005);
    this.info.intensity = I;
    this.info.playing = playing;
    this.info.restart = this.restart;
    comp.bar(this.info);
    this.restart = false;
    this.composerReady = true;
  }

  tick(now: number): void {
    const comp = this.composer;
    if (!comp) return;
    if (this.nextT < now - 0.25) {
      // Fell behind (stall / resume): realign on a fresh bar instead of machine-gunning notes.
      this.nextT = now + 0.05;
      this.stepIdx = 0;
    }
    const horizon = now + LOOKAHEAD;
    let guard = 0;
    let failure: unknown = null;
    while (this.nextT < horizon && guard++ < 64) {
      const i = this.stepIdx & 15;
      if (i === 0) {
        try {
          this.beginBar(this.nextT, now);
        } catch (e) {
          failure ??= e; // keep the clock running; report after the loop
        }
      }
      if (this.composerReady && this.nextT >= now - LATE) {
        try {
          comp.step(i, Math.max(this.nextT, now), this.info);
        } catch (e) {
          failure ??= e;
        }
      }
      this.nextT += this.info.sd;
      this.stepIdx++;
    }
    if (failure) throw failure;
  }

  dispose(): void {
    this.composer?.dispose();
    this.composer = null;
  }
}
